import { useState } from "react";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDate } from "@/lib/format-date";
import { Textarea } from "@/components/ui/textarea";
import {
  useExtendCalibrationDeadline,
  useReactivateCalibration,
} from "@/hooks/queries/useAdminUserActions";
import {
  toAdminUserError,
  type AdminUserBanner,
} from "@/routes/admin/admin-user-error";
import { requiredDate } from "@/routes/admin/create-organization-schema";

// Story 20.3 (AC11) — Screen 21's two FR86 calibration-deadline actions.
//
// ONE COMPONENT, TWO MODES. The extension and the reactivation take the same two
// fields, refuse on the same four codes and render the same submit button; what
// differs is the trigger label, the dialog title and WHICH ROUTE is called. A
// second near-identical file would be two copies of the past-date rule, the
// trim rule and the four refusal branches, drifting apart at the first edit.
//
// The `mode` prop selects between two NAMED hooks — it is never passed down into
// a path or a body. The audit event that lands on a seven-year append-only row is
// decided by the route, and the route is decided by a literal in this file
// (`useAdminUserActions.ts`, D3).
//
// THE CLIENT DOES NOT OWN THE CLOCK. There is no `min` attribute and no
// client-side past-date refusal, for the reason `ExtendContractControl.tsx:38-43`
// gives about the contract date and which applies here unchanged: the server
// compares the submitted calendar day against ITS OWN today, and a browser-local
// comparison disagrees with it by up to a day at the boundary — refusing dates
// the server would accept, from a control whose whole purpose is granting an
// exception. ADMIN-019 answers a past date, with copy that names the boundary
// (`admin-user-error.ts`).
//
// THE REASON IS REQUIRED and is the one thing this control does refuse locally
// (boundary row 11). That is not the clock: "is this box empty" needs no
// agreement with the server about what day it is, and the server applies the same
// trim. An extension is a discretionary exception granted against the platform's
// own competency programme, and FR86 records the date AND the reason.

export const EXTEND_CALIBRATION_TRIGGER_LABEL = "Extend calibration deadline";
/**
 * `Reactivate calibration`, never panel 4b's bare `Reactivate` (boundary row 10).
 * `AdminUserDetailScreen.tsx` already renders a button named exactly `Reactivate`
 * for Story 11.5's ACCOUNT reactivate, and the two sit in the same toolbar: an
 * unqualified name there is one operator mis-click between a blocked account and
 * a calibration clock, and one Playwright strict-mode failure in every spec that
 * asks for it by name.
 */
export const REACTIVATE_CALIBRATION_TRIGGER_LABEL = "Reactivate calibration";
/** Panel 4b's dialog sub-line reads `… · current deadline Oct 14, 2026`. */
export const CALIBRATION_DEADLINE_IN_FORCE_PREFIX = "Current deadline ";
export const CALIBRATION_DEADLINE_SUBMIT_LABEL = "Save new deadline";
export const CALIBRATION_DEADLINE_PENDING_LABEL = "Saving\u2026";
export const CALIBRATION_DEADLINE_FIELD_LABEL = "New calibration deadline";
export const CALIBRATION_REASON_FIELD_LABEL = "Reason / note";
/**
 * Verbatim from panel 4b's helper text. It is the one sentence that tells the
 * admin this free text is NOT confidential and NOT shown to the clinician —
 * withholding it is how a reason gets written that its author would not have
 * written knowing where it lands.
 */
export const CALIBRATION_REASON_HELP =
  "Recorded in the audit trail with your name and the time. Not shown to the clinician.";
export const CALIBRATION_DEADLINE_DATE_REQUIRED_MESSAGE =
  "Enter the new calibration deadline.";
export const CALIBRATION_REASON_REQUIRED_MESSAGE =
  "Enter a reason. It is recorded in the audit trail.";
/**
 * The server's `REASON_MAX_LENGTH` (both slices' `schemas.py`), mirrored so the
 * field cannot compose a request the server is certain to refuse. A literal on
 * each side — there is no shared schema across the wire — so the two can drift;
 * the drift is one-directional and safe (a smaller FE bound only refuses more),
 * and a widened server bound simply stops being offered here.
 */
export const CALIBRATION_REASON_MAX_LENGTH = 1000;

/**
 * One date grammar, one client source. `requiredDate` is imported from the
 * create-organization schema AS IT STANDS — it supplies both messages this
 * control raises locally: the missing-value one passed in here, and its own
 * "Enter a valid date." for anything that is not `YYYY-MM-DD`. It carries no
 * bound of any kind, which is exactly what is wanted here.
 */
const newDeadlineShape = requiredDate(CALIBRATION_DEADLINE_DATE_REQUIRED_MESSAGE);

export type CalibrationDeadlineMode = "extend" | "reactivate";

/**
 * Panel 4b's dialog is titled after the action, and so is this one. A shared
 * title ("Calibration deadline") would leave the two dialogs indistinguishable
 * once open — the same fields, the same submit — and the audit row they produce
 * is not the same.
 */
const TITLE: Record<CalibrationDeadlineMode, string> = {
  extend: EXTEND_CALIBRATION_TRIGGER_LABEL,
  reactivate: REACTIVATE_CALIBRATION_TRIGGER_LABEL,
};

const DESCRIPTION: Record<CalibrationDeadlineMode, string> = {
  // Panel 4b's own helper, which is what makes ONE date field sufficient: a short
  // fixed extension and a date far enough out to lift the deadline in practice
  // are the same write.
  extend:
    "The clinician sees the new date as their calibration deadline. A date far enough out lifts the deadline in practice.",
  // The reactivation's description names the state change, because that is the
  // part the date does not show: the hold is what lifts, and scoring returns.
  //
  // It names WHICH hold, and says in the same breath that it is the only one
  // that moves. AC10 forbids a string that claims the account is unheld in
  // general, and an unqualified "lifting the hold" is exactly that claim.
  //
  // The qualifier is DEFENSIVE — it is not a reference to a second hold that
  // exists. There is exactly ONE hold on calibration progression: the FR86
  // deadline hold Story 20.2 owns (`CALIB-002`), which is the one this write
  // lifts. FR85's three-unsuccessful-attempts rule, the only other hold this
  // platform ever contemplated, was removed by CITS on 2026-09-15, so no
  // attempt hold exists and none is planned; Story 8.14 is now FR85's
  // informational ±0.9 flag and owns no hold at all. The sentence is kept
  // because it costs one clause and stays true whatever is added later: a
  // super_admin told "the hold is lifted" about an account still held for some
  // other reason would have been told they restored scoring when they had not.
  // The wire is unchanged: there is no `hold_type` field and no enum
  // distinguishing holds, because AC10 puts the distinction in the COPY and
  // leaves 20.2 owning the state.
  reactivate:
    "Lifting the calibration-deadline hold restores scoring from the new date. Any other hold on this account is unaffected. The clinician sees the new date as their calibration deadline.",
};

export function ExtendCalibrationDeadlineControl({
  userId,
  mode,
  currentDeadline,
}: {
  userId: string;
  mode: CalibrationDeadlineMode;
  /**
   * The deadline in force. Rendered as the dialog's sub-line and NOWHERE else —
   * it is never seeded into the date field (DW-699). Optional and often absent:
   * the three dates are withheld from a caller who is not numeric-authorized
   * (AC12), and this control is super_admin-only, so in practice it is present —
   * but the type must not promise what the payload does not.
   */
  currentDeadline?: string;
}) {
  const [open, setOpen] = useState(false);
  // DW-699 — the date field starts EMPTY, and never on the deadline in force.
  // `currentDeadline` is the ONE value each server guard is built to refuse:
  // ADMIN-020 refuses `new_deadline == previous_deadline_at` in extend mode, and
  // the reactivate population is by definition clinicians whose deadline has
  // already elapsed, so ADMIN-019 (`new_deadline < today`, UTC) refuses the same
  // value for EVERY member of that cohort — and it is reached first.
  //
  // Nothing computed goes here either. Screen 38's panel-4b prose records the
  // client's 2026-09-07 ruling (issue #745): "Neither the full 60 days nor the
  // remainder of the original window is granted automatically", and a day-count
  // field and 7/30/60 chips were both rejected by name — so there is no
  // sanctioned offset to seed. And a browser-computed `today` would re-open
  // ADMIN-019 at the timezone boundary, for the same reason this control carries
  // no `min` (:41-47). An empty field composes no request at all: `requiredDate`
  // refuses it locally, instantly, in the admin's own words.
  const [draft, setDraft] = useState("");
  const [reason, setReason] = useState("");
  const [dateError, setDateError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [banner, setBanner] = useState<AdminUserBanner | null>(null);

  // BOTH hooks are created on every render and one is used — the rules of hooks
  // forbid the conditional call, and a `mode`-parameterised single hook is the
  // thing D3 rules out.
  const extend = useExtendCalibrationDeadline(userId);
  const reactivate = useReactivateCalibration(userId);
  const mutation = mode === "extend" ? extend : reactivate;

  const fieldId = `calibration-deadline-${mode}`;

  function openDialog() {
    // Cleared, never re-seeded (DW-699) — see the `draft` declaration above.
    setDraft("");
    setReason("");
    setDateError(null);
    setReasonError(null);
    setBanner(null);
    setOpen(true);
  }

  function onSubmit() {
    if (mutation.isPending) return;

    const parsedDate = newDeadlineShape.safeParse(draft);
    // Both fields are checked before either refusal is raised, so an admin who
    // left both empty is told so once rather than twice.
    const trimmedReason = reason.trim();
    const nextDateError = parsedDate.success
      ? null
      : (parsedDate.error.issues[0]?.message ?? CALIBRATION_DEADLINE_DATE_REQUIRED_MESSAGE);
    const nextReasonError = trimmedReason ? null : CALIBRATION_REASON_REQUIRED_MESSAGE;

    setDateError(nextDateError);
    setReasonError(nextReasonError);
    setBanner(null);
    if (nextDateError || nextReasonError || !parsedDate.success) return;

    // The TRIMMED reason is what is sent. The server trims too and would refuse
    // a whitespace-only one, but sending the untrimmed text would put the
    // admin's stray leading newline on a row nobody can edit afterwards.
    mutation.mutate(
      { new_deadline: parsedDate.data, reason: trimmedReason },
      {
        onSuccess: () => setOpen(false),
        onError: (error) => setBanner(toAdminUserError(error).banner),
      },
    );
  }

  return (
    <>
      <Button variant="secondary" size="sm" onClick={openDialog}>
        {TITLE[mode]}
      </Button>

      <Dialog open={open} onOpenChange={(next) => (next ? openDialog() : setOpen(false))}>
        <DialogContent className="w-[min(var(--width-md,520px),100%)]">
          <DialogHeader>
            <DialogTitle>{TITLE[mode]}</DialogTitle>
            <DialogDescription>{DESCRIPTION[mode]}</DialogDescription>
            {/* Panel 4b's dialog sub-line (`amps-screen-38…:606-608`), minus the
                identity half. The DEADLINE half is load-bearing and the identity
                half is not: this line is the ONLY copy of the deadline in force
                inside the dialog — the date field starts empty (DW-699) and the
                card that shows it is behind the overlay. That is the "picks a
                date against nothing" failure the four wire fields exist to
                prevent, reintroduced one layer up. The clinician's name and email are
                dropped because the dialog opens from THEIR card and the shipped
                house precedent beside it (`ExtendContractControl.tsx:131-136`)
                carries no identity sub-line either.

                Absent when the caller received no dates (AC12 withholds them
                from anyone not numeric-authorized). Rendering "current deadline
                —" there would state an absence of the deadline rather than an
                absence of permission to read it. */}
            {currentDeadline ? (
              <p className="text-sm text-[color:var(--text-secondary)] [font-variant-numeric:tabular-nums]">
                {`${CALIBRATION_DEADLINE_IN_FORCE_PREFIX}${formatDate(currentDeadline)}`}
              </p>
            ) : null}
          </DialogHeader>

          <div className="flex flex-col gap-1">
            <Label htmlFor={fieldId}>{CALIBRATION_DEADLINE_FIELD_LABEL}</Label>
            {/*
              A native calendar and NOTHING ELSE: no `min`, no day-count chips, no
              "+30 days" presets. A chip row would be a second grammar for the
              same field, and every preset is a date the client computed against
              a clock the server does not share.
            */}
            <Input
              id={fieldId}
              type="date"
              className="[font-variant-numeric:tabular-nums]"
              value={draft}
              aria-invalid={dateError ? true : undefined}
              aria-describedby={dateError ? `${fieldId}-error` : undefined}
              onChange={(event) => {
                setDraft(event.target.value);
                setDateError(null);
              }}
            />
            {dateError ? (
              <p
                id={`${fieldId}-error`}
                role="alert"
                className="text-sm text-[color:var(--text-error)]"
              >
                {dateError}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor={`${fieldId}-reason`}>{CALIBRATION_REASON_FIELD_LABEL}</Label>
            <Textarea
              id={`${fieldId}-reason`}
              value={reason}
              // Mirrors the server's `REASON_MAX_LENGTH` (both slices'
              // `schemas.py`). Without it the only way to learn the bound is a
              // 422 whose body echoes the first 200 characters of the rejected
              // reason back into the error payload and the structured log.
              maxLength={CALIBRATION_REASON_MAX_LENGTH}
              placeholder="e.g. 'Extended parental leave — agreed by email on Oct 20. Six additional weeks.'"
              aria-invalid={reasonError ? true : undefined}
              aria-describedby={
                reasonError ? `${fieldId}-reason-error` : `${fieldId}-reason-help`
              }
              onChange={(event) => {
                setReason(event.target.value);
                setReasonError(null);
              }}
            />
            <p
              id={`${fieldId}-reason-help`}
              className="text-sm text-[color:var(--text-secondary)]"
            >
              {CALIBRATION_REASON_HELP}
            </p>
            {reasonError ? (
              <p
                id={`${fieldId}-reason-error`}
                role="alert"
                className="text-sm text-[color:var(--text-error)]"
              >
                {reasonError}
              </p>
            ) : null}
          </div>

          {banner ? (
            <Banner variant={banner.variant} title={banner.title}>
              <p>{banner.body}</p>
              {banner.code ? (
                <p className="text-sm">{`Reference code: ${banner.code}`}</p>
              ) : null}
            </Banner>
          ) : null}

          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={mutation.isPending}>
              {mutation.isPending
                ? CALIBRATION_DEADLINE_PENDING_LABEL
                : CALIBRATION_DEADLINE_SUBMIT_LABEL}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
